import type { CaptureProfile, LocatedMetricRegion } from '@/domain/types'
import { drawSourceToImageData, isImageDataSource } from '@/core/image'
import { validArtaleBar } from '@/presets/artale/layout-locator'
import { validMxdcBar } from '@/presets/mxdc/layout-locator'
import { validGcwBar } from '@/presets/gcw/layout-locator'
import { validWorldsBar } from '@/presets/worlds/layout-locator'
import type { LayoutDetection } from '@/presets/types'
import type { DetectedPreset } from '@/core/preset-selection'

import { probeGameUi, type UiProbeResult } from './automatic-preset-probe'
export { probeGameUi } from './automatic-preset-probe'
export type { UiProbeResult } from './automatic-preset-probe'
export interface UiDecision { allow: boolean; kind: UiProbeResult['kind']; confirmed: boolean }

export async function probeGameUiAsync(frame: ImageData, signal?: AbortSignal): Promise<UiProbeResult> {
  if (signal?.aborted) return { kind: 'unknown' }
  if (typeof Worker === 'undefined') return probeGameUi(frame)
  let worker: Worker | undefined
  let cleanup: (() => void) | undefined
  try {
    worker = new Worker(new URL('./automatic-preset.worker.ts', import.meta.url), { type: 'module' })
    return await new Promise<UiProbeResult>((resolve, reject) => {
      const abort = () => reject(new DOMException('UI probe cancelled', 'AbortError'))
      const timer = setTimeout(() => reject(new Error('UI probe timed out')), 6_000)
      cleanup = () => { clearTimeout(timer); signal?.removeEventListener('abort', abort) }
      signal?.addEventListener('abort', abort, { once: true })
      worker!.onerror = () => reject(new Error('UI probe unavailable'))
      worker!.onmessageerror = () => reject(new Error('UI probe message failed'))
      worker!.onmessage = event => {
        clearTimeout(timer)
        if (event.data.error) reject(new Error(event.data.error))
        else resolve(event.data.result)
      }
      const data = new Uint8ClampedArray(frame.data)
      worker!.postMessage({ width: frame.width, height: frame.height, data }, [data.buffer])
    })
  } catch { return signal?.aborted ? { kind: 'unknown' } : probeGameUi(frame) }
  finally { cleanup?.(); worker?.terminate() }
}

/** Per capture/pipeline instance. Valid locked labels avoid another full search. */
export class AutomaticPresetMonitor {
  private candidate: { kind: DetectedPreset; at: number; layout?: LayoutDetection } | null = null
  private locked: { kind: DetectedPreset; layout: LayoutDetection } | null = null
  private dimensions = ''
  private nextProbeAt = 0
  private lockedUntil = 0
  private failures = 0
  private lastDecision: UiDecision | null = null
  private epoch = 0
  private controller = new AbortController()

  constructor(private readonly probe: (frame: ImageData, signal?: AbortSignal) => UiProbeResult | Promise<UiProbeResult> = probeGameUiAsync) {}
  reset(): void { this.controller.abort(); this.controller = new AbortController(); this.epoch++; this.candidate = null; this.locked = null; this.nextProbeAt = 0; this.failures = 0; this.lastDecision = null }

  async inspect(source: CanvasImageSource | ImageData, width: number, height: number, profile: CaptureProfile, at: number, force = false): Promise<UiDecision> {
    const dimensions = `${width}:${height}`
    if (dimensions !== this.dimensions) { this.reset(); this.dimensions = dimensions }
    const epoch = this.epoch
    if (!force && this.locked && this.locked.kind === profile.presetId && at < this.lockedUntil) {
      const validate = { artale: validArtaleBar, mxdc: validMxdcBar, gcw: validGcwBar, worlds: validWorldsBar }[this.locked.kind]
      const valid = (['hp', 'mp', 'exp'] as const).filter(metric => {
        const region: LocatedMetricRegion | undefined = this.locked!.layout.regions[metric]
        return region && validate(source, width, height, metric, region)
      }).length >= 2
      if (valid) return { allow: true, kind: this.locked.kind, confirmed: true }
      this.locked = null
    }
    if (!force && at < this.nextProbeAt) return this.lastDecision ?? { allow: true, kind: 'unknown', confirmed: false }
    const frame = isImageDataSource(source) ? source : drawSourceToImageData(source, width, height)
    const found = await this.probe(frame, this.controller.signal)
    if (epoch !== this.epoch) return { allow: false, kind: 'unknown', confirmed: false }
    if (found.kind === 'unknown' || found.kind === 'ambiguous') {
      this.candidate = null
      this.nextProbeAt = at + [2_000, 5_000, 10_000, 30_000][Math.min(this.failures++, 3)]!
      return this.lastDecision = { allow: found.kind === 'unknown' && this.lastDecision?.allow !== false, kind: found.kind, confirmed: false }
    }
    this.failures = 0; this.nextProbeAt = 0
    const confirmed = this.candidate?.kind === found.kind && at > this.candidate.at
    if (!this.candidate || at > this.candidate.at) this.candidate = { kind: found.kind, at, layout: found.layout }
    if (confirmed && found.layout) { this.locked = { kind: found.kind, layout: found.layout }; this.lockedUntil = at + 15_000 }
    return this.lastDecision = { allow: found.kind === profile.presetId, kind: found.kind, confirmed }
  }
}
